import type { JsonLdObject } from "@/lib/schema/json-ld"

export type StructuredDataProps = {
  data: JsonLdObject | JsonLdObject[]
  id?: string
}

/** Server component for rendering crawlable JSON-LD in the initial HTML.
 *  Uses dangerouslySetInnerHTML because that is the standard pattern for
 *  inline JSON-LD in Next.js: the JSON is server-controlled (built from
 *  schema builders, never from user input) and the `<` -> `<` escape
 *  prevents </script> injection. Bailing out early when `data` is undefined
 *  prevents JSON.stringify(undefined) -> undefined -> .replace crash. */
export function StructuredData({ data, id = "structured-data" }: StructuredDataProps) {
  if (!data) return null
  const graph = (Array.isArray(data) ? data : [data]).filter(Boolean)
  if (graph.length === 0) return null

  const payload = graph.length === 1 ? graph[0] : {
    "@context": "https://schema.org",
    "@graph": graph,
  }

  const serialized = JSON.stringify(payload)
  if (!serialized) return null
  const safeHtml = serialized.replace(/</g, "\\u003c")

  return (
    <script
      id={id}
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  )
}
